/* ===================== STUDENT OVERVIEW =====================
   Role-specific Dashboard screen for student (academy) accounts,
   rendered through TAB_ROLE_COMPONENTS in tabs/index.js. Students
   can't reach the consultation/horoscope/orders tabs the seeker
   overview links to (roles.js TAB_ACCESS), so their overview is
   learning-first: enrollment stats, the My Enrollments table and
   a profile quick view.

   Everything is live — no demo rows or fallback numbers: the stats
   and table ride GET /enrollments (useEnrollments — shared query
   with the Academy tab), Available Courses comes from GET /courses
   (same query key as the Academy tab's browse row, so switching
   there later costs nothing) and Profile Quick View reads the
   page-level useDashboard profile. Every value shows a pulsing
   skeleton while its query loads and an em dash / retry state when
   a read fails, never a made-up number. */
import { FiBookOpen, FiCheckCircle, FiLayers, FiPlayCircle } from 'react-icons/fi'
import { appUrl } from '@astro/config'
import { useCourseCatalog, useEnrollments } from '../useDashboard'

const courseHref = (ref) => appUrl('education', ref != null ? `/classes/${ref}` : '/classes')

export default function StudentOverviewTab({ onSwitch, data }) {
  const {
    enrollments, activeCount, hasEnrollments,
    isLoading: enrollmentsLoading, isError: enrollmentsError, refetch: refetchEnrollments,
  } = useEnrollments()
  const { total: catalogTotal, isLoading: catalogLoading, isError: catalogError } = useCourseCatalog(4)
  const completedCount = enrollments.filter((e) => e.completed).length
  const profile = data?.profile ?? {}
  const profileLoading = Boolean(data?.isLoading)

  /* Skeleton while the query loads, live number once it lands, em dash if
     the read failed — a failed read must never render as a real "0". */
  const enrollStat = (n) => (enrollmentsLoading ? null : enrollmentsError ? '—' : String(n))

  return (
    <div className="space-y-8">
      {/* ── Academy console strip — the student counterpart of the astrologer console ── */}
      <div className="relative overflow-hidden rounded-2xl bg-[#3D0A0A] text-white p-5 shadow-lg shadow-[#3D0A0A]/15">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#D48806]/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-12 left-1/3 w-44 h-44 bg-red-900/40 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[8px] uppercase font-bold tracking-[0.3em] text-[#D4A017]">Student Dashboard</p>
            <h2 className="text-lg font-royal text-[#FBF3E4] mt-1">Your learning at a glance</h2>
            <p className="text-[10px] text-white/50 mt-0.5">Track your progress and enrollments — pick up right where you left off.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={appUrl('education', "classes")}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[9px] uppercase tracking-[0.15em] px-4 py-2 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
            >
              Browse Courses
            </a>

            <button onClick={() => onSwitch('myprofile')} className="border border-white/15 hover:border-[#D4A017]/60 text-[#FBF3E4] hover:text-[#D4A017] font-semibold text-[9px] uppercase tracking-[0.15em] px-4 py-2 rounded-xl transition-all">My Profile</button>
          </div>
        </div>
      </div>

      {/* ── Learning stats — all four ride the live enrollment/catalog reads ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard tone="#3D0A0A" label="Enrolled Courses" icon={<FiBookOpen size={20} />}
          value={enrollStat(enrollments.length)}
          caption="View my courses"
          onClick={() => onSwitch('courses')} />
        <StatCard tone="#D48806" label="In Progress" icon={<FiPlayCircle size={20} />}
          value={enrollStat(activeCount)}
          caption="Resume learning"
          onClick={() => onSwitch('courses')} />
        <StatCard tone="#3D0A0A" label="Completed" icon={<FiCheckCircle size={20} />}
          value={enrollStat(completedCount)}
          caption="Finished courses"
          onClick={() => onSwitch('courses')} />
        <StatCard tone="#D48806" label="Available Courses" icon={<FiLayers size={20} />}
          value={catalogLoading ? null : catalogError ? '—' : String(catalogTotal)}
          caption="Browse catalogue"
          onClick={() => onSwitch('courses')} />
      </div>

      {/* ── My Enrollments — live table of the student's courses ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Academy</span>
            <h3 className="text-xl text-astroDark font-royal">My Enrollments</h3>
          </div>
          <button onClick={() => onSwitch('courses')} className="text-xs font-bold text-[#8B4513] uppercase tracking-wider hover:underline">View All</button>
        </div>

        {enrollmentsError ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🛰️</div>
            <p className="text-base font-semibold text-astroDark mb-1">Couldn&rsquo;t load your enrollments</p>
            <p className="text-sm text-stone-400 mb-4">Something went wrong while fetching your courses. Please try again.</p>
            <button
              type="button"
              onClick={() => refetchEnrollments()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
            >
              Retry
            </button>
          </div>
        ) : !enrollmentsLoading && !hasEnrollments ? (
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🎓</div>
            <p className="text-base font-semibold text-astroDark mb-1">No enrollments yet</p>
            <p className="text-sm text-stone-400 mb-4">Enroll in a course from the Vedic Academy and it will appear here.</p>
            <button onClick={() => onSwitch('courses')} className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all">
              Browse Courses
            </button>
          </div>
        ) : (
          <>
          {/* Below md the five columns become one card per enrollment — the
              min-w-[640px] table pushed Progress and the Resume action, the two
              things a student actually comes here for, off a phone screen. */}
          <div className="md:hidden space-y-3">
            {enrollmentsLoading
              ? [...Array(3)].map((_, i) => <EnrollmentSkeletonCard key={i} />)
              : enrollments.map((c) => (
                <div key={c.key} className="border border-stone-200 rounded-xl p-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <a href={courseHref(c.courseRef)} className="text-sm font-bold text-stone-900 leading-tight hover:text-[#8B4513] wrap-break-word">{c.title}</a>
                      {c.category ? <p className="text-[10px] text-stone-400 mt-0.5">{c.category}</p> : null}
                    </div>
                    <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${c.completed ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
                      {c.completed ? 'Completed' : 'Active'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <div className="h-1.5 flex-1 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full bg-linear-to-r rounded-full ${c.completed ? 'from-green-500 to-green-500' : 'from-goldAccent to-goldPrimary'}`}
                        style={{ width: `${Math.min(100, Math.max(0, c.progress))}%` }}
                      />
                    </div>
                    <span className="num text-[10px] font-bold text-stone-500 shrink-0">{Math.round(c.progress)}%</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-stone-100">
                    <span className="text-[10px] text-stone-500">Enrolled {c.enrolledOn ?? '—'}</span>
                    <a
                      href={courseHref(c.courseRef)}
                      className="shrink-0 border border-stone-200 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
                    >
                      {c.completed ? 'Review' : 'Resume'}
                    </a>
                  </div>
                </div>
              ))}
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[640px]">
              <thead>
                <tr className="bg-stone-50 border-y border-stone-100">
                  {['Course', 'Progress', 'Status', 'Enrolled', 'Action'].map((h, i) => (
                    <th key={h} className={`px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-stone-400 ${i === 4 ? 'text-right' : ''}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {enrollmentsLoading
                  ? [...Array(3)].map((_, i) => <EnrollmentSkeletonRow key={i} />)
                  : enrollments.map((c) => (
                    <tr key={c.key} className="border-b border-stone-100 hover:bg-amber-50/30 transition-colors">
                      <td className="px-4 py-3.5">
                        <a href={courseHref(c.courseRef)} className="text-sm font-bold text-stone-900 leading-tight hover:text-[#8B4513]">{c.title}</a>
                        {c.category ? <p className="text-[10px] text-stone-400 mt-0.5">{c.category}</p> : null}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-24 bg-stone-100 rounded-full overflow-hidden shrink-0">
                            <div
                              className={`h-full bg-linear-to-r rounded-full ${c.completed ? 'from-green-500 to-green-500' : 'from-goldAccent to-goldPrimary'}`}
                              style={{ width: `${Math.min(100, Math.max(0, c.progress))}%` }}
                            />
                          </div>
                          <span className="num text-[10px] font-bold text-stone-500">{Math.round(c.progress)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full ${c.completed ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
                          {c.completed ? 'Completed' : 'Active'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-xs font-semibold text-stone-600 whitespace-nowrap">{c.enrolledOn ?? '—'}</td>
                      <td className="px-4 py-3.5 text-right">
                        <a
                          href={courseHref(c.courseRef)}
                          className="inline-block border border-stone-200 px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50 whitespace-nowrap"
                        >
                          {c.completed ? 'Review Course' : 'Resume Learning'}
                        </a>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* ── Profile quick view — the page-level profile read, no extra fetch ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 mb-5">
          <h3 className="text-xl text-astroDark font-royal">Profile Quick View</h3>
          <button onClick={() => onSwitch('myprofile')} className="text-xs font-bold text-[#8B4513] uppercase tracking-wider hover:underline">Edit Profile</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4">
          <Fact label="Name" value={profile.name} loading={profileLoading} />
          <Fact label="Email" value={profile.email} loading={profileLoading} />
          <Fact label="Phone" value={profile.mobile} loading={profileLoading} />
        </div>
      </div>
    </div>
  )
}

/** One profile fact — tiny tracked label over the value, the dashboard's idiom. */
function Fact({ label, value, loading }) {
  return (
    <div>
      <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-stone-400">{label}</p>
      {loading ? (
        <div className="h-3.5 w-32 bg-stone-100 rounded-full mt-1.5 animate-pulse" />
      ) : (
        <p className="text-sm font-bold text-astroDark mt-1 break-words">{value || '—'}</p>
      )}
    </div>
  )
}

/* Pulsing placeholder matching one enrollment row — title, bar, pill,
   date and action button — shown while the enrollments read loads. */
function EnrollmentSkeletonRow() {
  return (
    <tr className="border-b border-stone-100 animate-pulse">
      <td className="px-4 py-3.5">
        <div className="h-3.5 w-40 bg-stone-100 rounded-full" />
        <div className="h-2.5 w-16 bg-stone-100 rounded-full mt-2" />
      </td>
      <td className="px-4 py-3.5"><div className="h-1.5 w-24 bg-stone-100 rounded-full" /></td>
      <td className="px-4 py-3.5"><div className="h-5 w-16 bg-stone-100 rounded-full" /></td>
      <td className="px-4 py-3.5"><div className="h-3 w-20 bg-stone-100 rounded-full" /></td>
      <td className="px-4 py-3.5"><div className="h-7 w-28 bg-stone-100 rounded-lg ml-auto" /></td>
    </tr>
  )
}

/** The same placeholder shaped for the mobile card list below md. */
function EnrollmentSkeletonCard() {
  return (
    <div className="border border-stone-200 rounded-xl p-3.5 animate-pulse">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-3.5 w-40 max-w-full bg-stone-100 rounded-full" />
          <div className="h-2.5 w-16 bg-stone-100 rounded-full" />
        </div>
        <div className="h-5 w-16 bg-stone-100 rounded-full shrink-0" />
      </div>
      <div className="h-1.5 w-full bg-stone-100 rounded-full mt-3" />
      <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-stone-100">
        <div className="h-3 w-24 bg-stone-100 rounded-full" />
        <div className="h-7 w-20 bg-stone-100 rounded-lg shrink-0" />
      </div>
    </div>
  )
}

/* Stat card on the design system's .stat-card surface (rounded, hover lift,
   top accent bar driven by --accent) — same surface and vertical layout as
   the seeker/astrologer overviews so all three dashboards read as one
   family: icon chip + label row, the live value (pulse placeholder while
   its query loads), then the caption tap target. */
function StatCard({ tone, label, value, caption, onClick, icon }) {
  return (
    <div className="stat-card h-full flex flex-col" style={{ '--accent': tone }}>
      <div className="flex items-center gap-3">
        <div className="stat-icon shrink-0 text-white" style={{ background: tone }}>
          {icon}
        </div>
        <p className="text-stone-500 text-[11px] font-bold uppercase tracking-wider leading-snug">{label}</p>
      </div>
      {value == null ? (
        <div className="h-6 w-16 bg-stone-100 rounded-full mt-3.5 mb-1 animate-pulse" />
      ) : (
        <h3 className="text-[22px] font-bold text-stone-900 mt-3 leading-tight break-words">{value}</h3>
      )}
      {onClick ? (
        <button onClick={onClick} className="mt-1 w-max text-left text-xs font-semibold text-[#A67C00] hover:underline cursor-pointer">{caption}</button>
      ) : (
        <span className="mt-1 text-xs font-semibold text-[#A67C00]">{caption}</span>
      )}
    </div>
  )
}
